import React, { createContext, useContext, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { handleFileUpload } from '../utils/receiptsUtils';
import UploadProgressSnackbar from '../components/receiptsdetail/UploadProgressSnackbar';
import { useNavigate } from 'react-router-dom';

const UploadContext = createContext();

export function UploadProvider({ children }) {
    const [uploadProgress, setUploadProgress] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [resetDismissed, setResetDismissed] = useState(0);
    const navigate = useNavigate();

    // Helper function to update file status
    const updateFileStatus = useCallback((fileId, status, receiptUuid = null, errorMessage = null) => {
        setUploadProgress(prev =>
            prev.map(file =>
                file.fileId === fileId ? { ...file, status, receiptUuid, errorMessage } : file
            )
        );
    }, []);

    const handleUpload = useCallback(async (files, token, onComplete) => {
        if (!files || files.length === 0) return;

        // Add a unique ID to each file for tracking
        const filesWithIds = Array.from(files).map(file => ({
            file,
            id: uuidv4()
        }));

        const newUploadProgress = filesWithIds.map(({ file, id }) => ({
            fileName: file.name,
            fileId: id,
            status: 'uploading',
            receiptUuid: null,
            errorMessage: null,
        }));

        // Clear any existing upload progress and start fresh
        setUploadProgress(newUploadProgress);
        setIsUploading(true);

        try {
            const uploadPromises = filesWithIds.map(({ file, id }) =>
                handleFileUpload(file, token, {
                    onUploadStart: () => {},
                    onUploadSuccess: () => {},
                    onUploadError: (message) => {
                        updateFileStatus(id, 'error', null, message);
                    },
                    onUploadComplete: async () => {
                        if (onComplete) {
                            await onComplete();
                        }
                    },
                }).then(uploadedReceiptData => {
                    updateFileStatus(id, 'completed', uploadedReceiptData.receipt_uuid);
                    return uploadedReceiptData;
                }).catch(error => {
                    console.error('Error uploading file:', error);
                    const errorMessage = error.message || 'Failed to upload receipt. Please try again.';
                    updateFileStatus(id, 'error', null, errorMessage);
                })
            );

            await Promise.all(uploadPromises);
        } finally {
            // Always set isUploading to false after all uploads are complete
            setIsUploading(false);
        }
    }, [updateFileStatus]);

    const handleRetryUpload = useCallback(async (file, token) => {
        // Get the original file from the input if possible
        const fileInput = document.querySelector('input[type="file"]');
        let fileToUpload = null;
        
        if (fileInput && fileInput.files) {
            // Try to find the file with the same name
            for (let i = 0; i < fileInput.files.length; i++) {
                if (fileInput.files[i].name === file.fileName) {
                    fileToUpload = fileInput.files[i];
                    break;
                }
            }
        }
        
        if (!fileToUpload) {
            // If we can't find the original file, prompt the user to select it again
            fileInput.click();
            return;
        }
        
        // Update the file status to uploading
        updateFileStatus(file.fileId, 'uploading');
        
        // Retry the upload
        handleFileUpload(fileToUpload, token, {
            onUploadStart: () => {},
            onUploadSuccess: () => {},
            onUploadError: (message) => {
                updateFileStatus(file.fileId, 'error', null, message);
            },
            onUploadComplete: async () => {
                // Refresh the receipts list
                const event = new CustomEvent('receiptsUpdated');
                window.dispatchEvent(event);
            },
        }).then(uploadedReceiptData => {
            updateFileStatus(file.fileId, 'completed', uploadedReceiptData.receipt_uuid);
            return uploadedReceiptData;
        }).catch(error => {
            console.error('Error uploading file:', error);
            const errorMessage = error.message || 'Failed to upload receipt. Please try again.';
            updateFileStatus(file.fileId, 'error', null, errorMessage);
        });
    }, []);

    const clearUploadProgress = useCallback(() => {
        setUploadProgress([]);
        setIsUploading(false);
        // Clear any file inputs
        const fileInputs = document.querySelectorAll('input[type="file"]');
        fileInputs.forEach(input => {
            input.value = '';
        });
        // Increment resetDismissed to trigger a reset of the snackbar's dismissed state
        setResetDismissed(prev => prev + 1);
    }, []);

    const handleViewReceipt = useCallback((receiptUuid) => {
        navigate(`/receipts/${receiptUuid}`);
    }, [navigate]);

    const value = {
        uploadProgress,
        isUploading,
        handleUpload,
        handleRetryUpload,
        clearUploadProgress,
        handleViewReceipt
    };

    return (
        <UploadContext.Provider value={value}>
            {children}
            <UploadProgressSnackbar
                open={uploadProgress.length > 0}
                uploadProgress={uploadProgress}
                allUploadsCompleted={uploadProgress.length > 0 && uploadProgress.every(file => file.status === 'completed')}
                onViewReceipt={handleViewReceipt}
                onClose={clearUploadProgress}
                onRetry={(file) => handleRetryUpload(file, localStorage.getItem('token'))}
                resetDismissed={resetDismissed}
            />
        </UploadContext.Provider>
    );
}

export function useUpload() {
    const context = useContext(UploadContext);
    if (!context) {
        throw new Error('useUpload must be used within an UploadProvider');
    }
    return context;
} 